import { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";

const iso = (d) => d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const today = new Date();
const weekAgo = new Date(Date.now() - 6 * 864e5);
const hrs = (m) => `${Math.floor(m / 60)}h ${String(Math.round(m % 60)).padStart(2, "0")}m`;

export default function ShiftHours() {
  const [f, setF] = useState({ date_from: iso(weekAgo), date_to: iso(today), driver_id: "" });
  const [drivers, setDrivers] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const params = useMemo(() => ({ date_from: f.date_from, date_to: f.date_to, ...(f.driver_id ? { driver_id: f.driver_id } : {}) }), [f.date_from, f.date_to, f.driver_id]);

  const load = useCallback(() => {
    setData(null);
    api.get("/reports/shift-hours", { params }).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData({ rows: [], totals: {} }); });
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/users").then((r) => setDrivers(r.data.filter((u) => u.role === "DRIVER"))).catch(() => {}); }, []);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/shift-hours.csv", { params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = `lfm_shift_hours_${f.date_from}_to_${f.date_to}.csv`; a.click();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const rows = data?.rows || [];
  return (
    <div data-testid="shift-hours-page">
      <PageHeader title="Shift Hours" subtitle="Driver shift and break minutes per day, for payroll. Times in Eastern Time."
        actions={<Button onClick={download} disabled={busy || !rows.length} data-testid="shift-hours-download">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4 mr-1.5" /> Download CSV</>}</Button>} />
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="shift-hours-from" /></div>
        <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="shift-hours-to" /></div>
        <div className="space-y-1"><Label className="text-xs">Driver</Label>
          <select className="h-9 rounded-md border border-input bg-transparent px-2 text-sm min-w-[180px]" value={f.driver_id} onChange={(e) => setF({ ...f, driver_id: e.target.value })} data-testid="shift-hours-driver">
            <option value="">All drivers</option>{drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select></div>
      </div>
      {data && (
        <div className="grid grid-cols-3 gap-3 mb-5 max-w-xl">
          {[["Shift time", data.totals.shift_minutes], ["Break time", data.totals.break_minutes], ["Paid time", data.totals.paid_minutes]].map(([l, v]) => (
            <div key={l} className="border border-border rounded-lg bg-card p-4" data-testid={`shift-total-${l.split(" ")[0].toLowerCase()}`}><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</p><p className="font-heading text-xl font-bold mt-1">{hrs(v || 0)}</p></div>
          ))}
        </div>
      )}
      <div className="border border-border rounded-lg bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="shift-hours-table">
          <thead><tr className="text-[11px] uppercase tracking-wider text-muted-foreground text-left border-b border-border">
            {["Date", "Driver", "Shifts", "In", "Out", "Shift", "Breaks", "Break", "Paid"].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {!data && <tr><td colSpan={9} className="p-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></td></tr>}
            {data && !rows.length && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground"><Clock className="w-5 h-5 mx-auto mb-1 opacity-50" />No shifts in this range.</td></tr>}
            {rows.map((r) => (
              <tr key={`${r.driver_id}-${r.date}`} className="border-b border-border last:border-0" data-testid="shift-hours-row">
                <td className="p-3 whitespace-nowrap">{r.date}</td><td className="p-3 font-semibold">{r.driver_name}</td><td className="p-3">{r.shifts}</td>
                <td className="p-3">{r.first_clock_in}</td><td className="p-3">{r.open_shift ? <span className="text-ochre font-semibold">On shift</span> : r.last_clock_out}</td>
                <td className="p-3">{hrs(r.shift_minutes)}</td><td className="p-3">{r.breaks}</td><td className="p-3">{hrs(r.break_minutes)}</td><td className="p-3 font-semibold">{hrs(r.paid_minutes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Paid time = shift time minus breaks. Shifts still open are counted up to now and flagged “On shift”.</p>
    </div>
  );
}
